import { useState, useEffect } from 'react';
import { useConfirmDialog } from '../components/ui/ConfirmDialog';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { UserBadge } from '../components/UserBadge';
import { Textarea } from '../components/ui/textarea';
import { 
  Loader2, ArrowLeft, Dumbbell, Trophy, Users, Heart,
  MessageCircle, UserPlus, UserMinus, UserCheck, Send, Lock, X, Clock, Crown
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { PostCard } from '../components/social';
import { getImageUrl } from '../utils/imageUtils';
import { getDifficultyLabel } from '../utils/constants';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/logo.webp';

const goalLabels = {
  weight_loss: 'Weight Loss',
  muscle_building: 'Muscle Building',
  endurance: 'Endurance',
  general_fitness: 'General Fitness',
  strength: 'Strength',
  core_strength: 'Core Strength'
};

export const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [activeStatPanel, setActiveStatPanel] = useState(null);
  const [statPanelData, setStatPanelData] = useState([]);
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const [loadingPanel, setLoadingPanel] = useState(false);
  const [sendingBuddyRequest, setSendingBuddyRequest] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await api().get(`/social/users/${userId}`);
      setProfile(res.data);
      setIsFollowing(res.data.is_following);
    } catch (err) {
      toast.error('Failed to load profile');
      navigate('/social');
    }
    setLoading(false);
  };

  const openStatPanel = async (panel) => {
    if (activeStatPanel === panel) {
      setActiveStatPanel(null);
      return;
    }
    setActiveStatPanel(panel);
    setLoadingPanel(true);
    setStatPanelData([]);
    try {
      if (panel === 'followers') {
        const res = await api().get(`/social/users/${userId}/followers`);
        setStatPanelData(res.data.followers || res.data || []);
      } else if (panel === 'following') {
        const res = await api().get(`/social/users/${userId}/following`);
        setStatPanelData(res.data.following || res.data || []);
      } else if (panel === 'workouts') {
        const res = await api().get(`/social/users/${userId}/workouts`);
        setStatPanelData(res.data.workouts || res.data || []);
      } else if (panel === 'achievements') {
        setStatPanelData(achievements || []);
      }
    } catch {
      setStatPanelData([]);
    }
    setLoadingPanel(false);
  };

  const handleFollow = async () => {
    try {
      const followStatus = profile?.follow_status;

      // Guard the destructive "unfollow" tap — easy to hit by accident on
      // mobile since the "Following" button sits where "Follow" used to be.
      if (followStatus === 'accepted') {
        const name = profile?.name || 'this user';
        const ok = await confirm({
          title: `Unfollow ${name}?`,
          description: "You'll stop seeing their posts and workout activity in your feed.",
          confirmLabel: 'Unfollow',
          cancelLabel: 'Keep Following',
          tone: 'danger',
        });
        if (!ok) return;
      }

      if (followStatus === 'accepted' || followStatus === 'pending') {
        // Unfollow or cancel request
        await api().delete(`/social/follow/${userId}`);
        toast.success(followStatus === 'accepted' ? 'Unfollowed' : 'Request cancelled');
      } else {
        // Send follow request
        await api().post('/social/follow', { user_id: userId });
        toast.success('Follow request sent!');
      }
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update follow status');
    }
  };

  const handleSendMessage = async () => {
    if (!messageContent.trim()) return;
    
    setSendingMessage(true);
    try {
      await api().post(`/direct-messages/send/${userId}`, {
        content: messageContent.trim()
      });
      toast.success('Message sent!');
      setMessageContent('');
      setShowMessageModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    }
    setSendingMessage(false);
  };

  const handleSendBuddyRequest = async () => {
    setSendingBuddyRequest(true);
    try {
      const res = await api().post('/buddies/request', { user_id: userId });
      if (res.data?.status === 'accepted') {
        toast.success("You're already buddies!");
      } else {
        toast.success('Buddy request sent!');
      }
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send buddy request');
    }
    setSendingBuddyRequest(false);
  };

  const handleAcceptBuddyRequest = async () => {
    if (!buddy_request_id) {
      toast.error('Missing request ID');
      return;
    }
    setSendingBuddyRequest(true);
    try {
      await api().post(`/buddies/requests/${buddy_request_id}/accept`);
      toast.success('Buddy request accepted!');
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept buddy request');
    }
    setSendingBuddyRequest(false);
  };

  const handleRejectBuddyRequest = async () => {
    if (!buddy_request_id) return;
    setSendingBuddyRequest(true);
    try {
      await api().post(`/buddies/requests/${buddy_request_id}/reject`);
      toast.success('Buddy request declined');
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline buddy request');
    }
    setSendingBuddyRequest(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-primary animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white">Profile not found</p>
      </div>
    );
  }

  const { user, stats, achievements, recent_posts, is_own_profile, can_message, follows_me, follow_status, buddy_status, buddy_request_outgoing, buddy_request_id } = profile;

  return (
    <div className="min-h-screen bg-black" data-testid="user-profile-page">
      {confirmDialog}
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white hover:text-primary transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Back</span>
          </button>
          <img src={LOGO_URL} alt="Ascend & Surpass" className="h-10 w-10" />
          <div className="w-20" />
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-card border border-border rounded-2xl p-6 text-center card-hover">
              {/* Avatar with milestone badges */}
              <div className="relative inline-block mb-4">
                <Avatar 
                  className="h-32 w-32 border-4 border-primary cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => user.profile_picture && setEnlargedImage(getImageUrl(user.profile_picture))}
                >
                  <AvatarImage src={getImageUrl(user.profile_picture)} />
                  <AvatarFallback className="bg-primary/20 text-primary text-4xl">
                    {user.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                {(user.badges || []).length > 0 && (
                  <div className="absolute -top-2 -right-2 flex flex-col gap-1">
                    {(user.badges || []).slice(0, 3).map((code) => (
                      <UserBadge key={code} code={code} size="lg" />
                    ))}
                  </div>
                )}
              </div>

              {/* Name */}
              <h1 className="font-heading text-2xl text-white mb-2">{user.name}</h1>
              
              {/* Premium Badge */}
              {user.is_premium && (
                <div className="flex justify-center mb-3">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border border-yellow-500/50 rounded-full">
                    <Crown className="h-4 w-4 text-yellow-400" />
                    <span className="text-yellow-400 font-heading text-xs uppercase tracking-wider">Premium</span>
                  </div>
                </div>
              )}
              
              {/* Badges */}
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {follows_me && (
                  <Badge className="bg-blue-500/20 text-blue-400">Follows you</Badge>
                )}
                {user.experience_level && (
                  <Badge className="bg-white/10 text-white capitalize">{getDifficultyLabel(user.experience_level)}</Badge>
                )}
              </div>

              {/* Goals */}
              {user.fitness_goals?.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 mb-6">
                  {user.fitness_goals.map(goal => (
                    <Badge key={goal} className="bg-primary/20 text-primary text-xs">
                      {goalLabels[goal] || goal}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div 
                  className={`bg-black p-4 cursor-pointer hover:bg-white/5 transition-colors border ${activeStatPanel === 'workouts' ? 'border-primary' : 'border-transparent'}`}
                  onClick={() => openStatPanel('workouts')}
                  data-testid="stat-workouts"
                >
                  <Dumbbell className="h-6 w-6 text-primary mx-auto mb-2" />
                  <p className="font-heading text-2xl text-white">{stats.workouts}</p>
                  <p className="text-muted-foreground text-sm">Workouts</p>
                </div>
                <div 
                  className={`bg-black p-4 cursor-pointer hover:bg-white/5 transition-colors border ${activeStatPanel === 'achievements' ? 'border-yellow-400' : 'border-transparent'}`}
                  onClick={() => openStatPanel('achievements')}
                  data-testid="stat-achievements"
                >
                  <Trophy className="h-6 w-6 text-yellow-400 mx-auto mb-2" />
                  <p className="font-heading text-2xl text-white">{achievements?.length || 0}</p>
                  <p className="text-muted-foreground text-sm">Achievements</p>
                </div>
                <div 
                  className={`bg-black p-4 cursor-pointer hover:bg-white/5 transition-colors border ${activeStatPanel === 'followers' ? 'border-blue-400' : 'border-transparent'}`}
                  onClick={() => openStatPanel('followers')}
                  data-testid="stat-followers"
                >
                  <Users className="h-6 w-6 text-blue-400 mx-auto mb-2" />
                  <p className="font-heading text-2xl text-white">{stats.followers}</p>
                  <p className="text-muted-foreground text-sm">Followers</p>
                </div>
                <div 
                  className={`bg-black p-4 cursor-pointer hover:bg-white/5 transition-colors border ${activeStatPanel === 'following' ? 'border-red-400' : 'border-transparent'}`}
                  onClick={() => openStatPanel('following')}
                  data-testid="stat-following"
                >
                  <Heart className="h-6 w-6 text-red-400 mx-auto mb-2" />
                  <p className="font-heading text-2xl text-white">{stats.following}</p>
                  <p className="text-muted-foreground text-sm">Following</p>
                </div>
              </div>

              {/* Stat Detail Panel */}
              <AnimatePresence>
                {activeStatPanel && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-6 overflow-hidden"
                  >
                    <div className="bg-black border border-border p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-heading text-sm text-white uppercase tracking-wider">
                          {activeStatPanel === 'workouts' && 'Workouts'}
                          {activeStatPanel === 'achievements' && 'Achievements'}
                          {activeStatPanel === 'followers' && 'Followers'}
                          {activeStatPanel === 'following' && 'Following'}
                        </h4>
                        <button onClick={() => setActiveStatPanel(null)} className="text-muted-foreground hover:text-white">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      {loadingPanel ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="h-5 w-5 text-primary animate-spin" />
                        </div>
                      ) : statPanelData.length === 0 ? (
                        <p className="text-muted-foreground text-sm text-center py-3">None yet</p>
                      ) : (activeStatPanel === 'followers' || activeStatPanel === 'following') ? (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {statPanelData.map((u) => (
                            <Link 
                              key={u.id} 
                              to={`/user/${u.id}`}
                              className="flex items-center gap-3 p-2 hover:bg-white/5 transition-colors rounded"
                            >
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={getImageUrl(u.profile_picture)} />
                                <AvatarFallback className="bg-primary/20 text-primary text-xs">{u.name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <span className="text-white text-sm">{u.name}</span>
                            </Link>
                          ))}
                        </div>
                      ) : activeStatPanel === 'achievements' ? (
                        <div className="flex flex-wrap gap-2">
                          {statPanelData.map((ach, idx) => (
                            <Badge key={ach.achievement_id || idx} className="bg-yellow-500/20 text-yellow-400">
                              {ach.achievement_id?.replace(/_/g, ' ') || 'Achievement'}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {statPanelData.map((w, idx) => (
                            <Link 
                              key={w.id || idx}
                              to={`/workout/${w.workout_id || w.id}`}
                              className="flex items-center justify-between p-2 hover:bg-white/5 transition-colors rounded"
                            >
                              <div className="flex items-center gap-2">
                                <Dumbbell className="h-4 w-4 text-primary" />
                                <span className="text-white text-sm">{w.workout_name || w.title || 'Workout'}</span>
                              </div>
                              {w.completed_at && (
                                <span className="text-muted-foreground text-xs">
                                  <Clock className="h-3 w-3 inline mr-1" />
                                  {new Date(w.completed_at).toLocaleDateString()}
                                </span>
                              )}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              {!is_own_profile && (
                <div className="space-y-3">
                  <Button
                    onClick={handleFollow}
                    className={`w-full ${
                      follow_status === 'accepted' 
                        ? 'bg-white/10 text-white hover:bg-red-500/20 hover:text-red-400' 
                        : follow_status === 'pending'
                        ? 'bg-yellow-500/20 text-yellow-400 hover:bg-red-500/20 hover:text-red-400'
                        : 'bg-primary text-black'
                    }`}
                    data-testid="follow-button"
                  >
                    {follow_status === 'accepted' ? (
                      <>
                        <UserMinus className="h-4 w-4 mr-2" />
                        Following
                      </>
                    ) : follow_status === 'pending' ? (
                      <>
                        <Clock className="h-4 w-4 mr-2" />
                        Requested
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Follow
                      </>
                    )}
                  </Button>

                  {/* Buddy actions — only meaningful once they follow each other */}
                  {buddy_status === 'accepted' ? (
                    <Button variant="outline" disabled className="w-full bg-primary/10 text-primary border-primary/40 disabled:opacity-100" data-testid="buddy-status-buddies">
                      <UserCheck className="h-4 w-4 mr-2" />
                      Workout Buddies
                    </Button>
                  ) : buddy_status === 'pending' ? (
                    buddy_request_outgoing ? (
                      <Button variant="outline" disabled className="w-full disabled:opacity-100" data-testid="buddy-status-pending-outgoing">
                        <Clock className="h-4 w-4 mr-2" />
                        Buddy Request Sent
                      </Button>
                    ) : (
                      <div className="flex gap-2 w-full">
                        <Button
                          onClick={handleAcceptBuddyRequest}
                          disabled={sendingBuddyRequest}
                          className="flex-1 bg-orange-500 text-black hover:bg-orange-400"
                          data-testid="buddy-accept-btn"
                        >
                          {sendingBuddyRequest ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Users className="h-4 w-4 mr-2" />
                          )}
                          Accept Buddy
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleRejectBuddyRequest}
                          disabled={sendingBuddyRequest}
                          className="flex-1"
                          data-testid="buddy-decline-btn"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Decline
                        </Button>
                      </div>
                    )
                  ) : (isFollowing && follows_me) ? (
                    <Button
                      onClick={handleSendBuddyRequest}
                      disabled={sendingBuddyRequest}
                      className="w-full bg-orange-500 text-black hover:bg-orange-400"
                      data-testid="add-buddy-button"
                    >
                      {sendingBuddyRequest ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Users className="h-4 w-4 mr-2" />
                      )}
                      Add as Buddy
                    </Button>
                  ) : follow_status === 'accepted' && !follows_me ? (
                    <div className="text-center text-muted-foreground text-xs p-3 bg-white/5 border border-border" data-testid="buddy-need-mutual-follow">
                      <Users className="h-4 w-4 mx-auto mb-1 opacity-60" />
                      Once they follow you back, you can add them as a workout buddy
                    </div>
                  ) : null}
                  
                  {can_message ? (
                    <Button
                      onClick={() => setShowMessageModal(true)}
                      variant="outline"
                      className="w-full"
                      data-testid="send-message-button"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Send Message
                    </Button>
                  ) : follow_status === 'accepted' ? (
                    <Button variant="outline" disabled className="w-full opacity-50">
                      <Lock className="h-4 w-4 mr-2" />
                      Messages Disabled
                    </Button>
                  ) : (
                    <div className="text-center text-muted-foreground text-sm p-3 bg-white/5 border border-border">
                      <Lock className="h-4 w-4 mx-auto mb-1" />
                      {follow_status === 'pending' 
                        ? 'Waiting for follow request approval to message'
                        : 'Follow to message this user'
                      }
                    </div>
                  )}
                  
                  {follow_status === 'accepted' && (
                    <Link to={`/dm/${userId}`}>
                      <Button variant="ghost" className="w-full text-muted-foreground">
                        View Conversation
                      </Button>
                    </Link>
                  )}
                </div>
              )}

              {is_own_profile && (
                <Link to="/profile">
                  <Button className="w-full bg-primary text-black">
                    Edit Profile
                  </Button>
                </Link>
              )}
            </div>

            {/* Achievements */}
            {achievements?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-card border border-border rounded-2xl p-6 mt-6 card-hover"
              >
                <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-400" />
                  Achievements
                </h3>
                <div className="flex flex-wrap gap-2">
                  {achievements.slice(0, 6).map((ach, idx) => (
                    <Badge 
                      key={ach.achievement_id || idx}
                      className="bg-yellow-500/20 text-yellow-400"
                    >
                      {ach.achievement_id?.replace(/_/g, ' ') || 'Achievement'}
                    </Badge>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Recent Posts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            <h2 className="font-heading text-xl text-white uppercase tracking-wider mb-6">
              Recent Posts
            </h2>
            
            {recent_posts?.length > 0 ? (
              <div className="space-y-4">
                {recent_posts.map(post => (
                  <PostCard 
                    key={post.id} 
                    post={{...post, user}} 
                    currentUserId={currentUser?.id}
                    api={api}
                    onUpdate={fetchProfile}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-card border border-border rounded-2xl p-12 text-center">
                <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No posts yet</p>
              </div>
            )}
          </motion.div>
        </div>
      </main>

      {/* Send Message Modal */}
      <AnimatePresence>
        {showMessageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setShowMessageModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card border border-border rounded-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="font-heading text-lg text-white uppercase tracking-wider">
                  Message {user.name}
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowMessageModal(false)}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="p-4">
                <Textarea
                  placeholder="Type your message..."
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  className="bg-black border-white/20 min-h-[120px] mb-4"
                  maxLength={1000}
                />
                
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-sm">
                    {messageContent.length}/1000
                  </span>
                  <Button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || !messageContent.trim()}
                    className="bg-primary text-black"
                  >
                    {sendingMessage ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Send
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Lightbox */}
      <AnimatePresence>
        {enlargedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
            onClick={() => setEnlargedImage(null)}
            data-testid="image-lightbox"
          >
            <button
              onClick={() => setEnlargedImage(null)}
              className="absolute top-6 right-6 text-white hover:text-primary transition-colors"
              data-testid="lightbox-close"
            >
              <X className="h-8 w-8" />
            </button>
            <motion.img
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              src={enlargedImage}
              alt="Profile"
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserProfilePage;
